import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { COMPONENT_OUTLINE_BORDER } from "../theme/componentBorders";
/**
 * 通用统计卡片组件
 * 根据传入的数据动态渲染不同数量的统计卡片
 * 不依赖 antd，使用原生 HTML 和 CSS
 */
const GenericCardsStatistic = ({ data = [], columns = 4, gutter = [16, 0], cardStyle = {}, showCards = true, className = "", style = {}, }) => {
    // 计算每个卡片占用的列数
    const getColSpan = () => {
        return 24 / columns;
    };
    // 渲染单个统计卡片
    const renderStatisticCard = (item) => {
        // 判断是否可点击
        const isClickable = item.clickable === true || !!item.onClick;
        // 处理点击事件
        const handleClick = (event) => {
            console.log(`[GenericCardsStatistic] Click handler called for card ${item.key}`, {
                isClickable,
                hasOnClick: !!item.onClick,
                event,
            });
            if (isClickable && item.onClick) {
                console.log(`[GenericCardsStatistic] Calling item.onClick for card ${item.key}`);
                item.onClick(item, event);
            }
            else {
                console.log(`[GenericCardsStatistic] onClick not called for card ${item.key}`, {
                    isClickable,
                    hasOnClick: !!item.onClick,
                });
            }
        };
        // 处理键盘事件（用于可访问性）
        const handleKeyDown = (event) => {
            if (isClickable && item.onClick && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                item.onClick(item, event);
            }
        };
        // 处理鼠标移入事件
        const handleMouseEnter = (event) => {
            // 可以在这里添加鼠标移入的逻辑
        };
        // 统一缩小字号：标题更小、数值略小；允许调用方通过 valueStyle 覆盖
        const mergedValueStyle = {
            fontSize: 22,
            fontWeight: 600,
            color: "#262626",
            ...(item.valueStyle || {}),
        };
        // 处理图标
        const prefixIcon = item.prefix;
        // 格式化数值
        const formatValue = (value) => {
            if (typeof value === "number") {
                if (item.precision !== undefined) {
                    return value.toFixed(item.precision);
                }
                return value.toString();
            }
            return value;
        };
        const statisticContent = (_jsxs("div", { className: "statistic-content", style: {
                textAlign: "center",
                padding: "16px",
                ...item.statisticProps?.style,
            }, ...item.statisticProps, children: [_jsx("div", { className: "statistic-title", style: {
                        fontSize: 12,
                        color: "#8c8c8c",
                        marginBottom: "8px",
                    }, children: item.title }), _jsxs("div", { className: "statistic-value", style: mergedValueStyle, children: [prefixIcon && (_jsx("span", { style: { marginRight: "8px", fontSize: "20px" }, children: prefixIcon })), item.formatter
                            ? item.formatter(item.value, item)
                            : formatValue(item.value), item.suffix && (_jsx("span", { style: { fontSize: "14px", marginLeft: "4px" }, children: item.suffix }))] })] }));
        // 如果显示卡片包装，则用Card包装
        if (showCards) {
            // 先提取样式，避免 cursor 被覆盖
            const { style: cardPropsStyle, ...restCardProps } = item.cardProps || {};
            // 合并样式，确保 cursor 在最后，不会被其他样式覆盖
            const mergedCardStyle = {
                background: "#fff",
                borderRadius: "6px",
                boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.02), 0 1px 6px -1px rgba(0, 0, 0, 0.01), 0 2px 4px 0 rgba(0, 0, 0, 0.01)",
                border: COMPONENT_OUTLINE_BORDER,
                ...cardStyle,
                ...cardPropsStyle,
                // cursor 放在最后，如果可点击则强制设置为 pointer，确保不会被其他样式覆盖
                cursor: isClickable ? "pointer" : undefined,
            };
            // 提取其他 cardProps，避免覆盖我们的属性
            const { onClick: cardPropsOnClick, onKeyDown: cardPropsOnKeyDown, onMouseEnter: cardPropsOnMouseEnter, ...otherCardProps } = restCardProps;
            // 合并事件处理器
            const handleCardClick = (event) => {
                console.log(`[GenericCardsStatistic] Card clicked: ${item.key}`, {
                    isClickable,
                    hasOnClick: !!item.onClick,
                    hasCardPropsOnClick: !!cardPropsOnClick,
                    event,
                });
                // 先执行我们的 handleClick（如果可点击）
                if (isClickable && item.onClick) {
                    console.log(`[GenericCardsStatistic] Calling item.onClick for card ${item.key}`);
                    item.onClick(item, event);
                }
                // 然后执行 item.cardProps 的 onClick（如果存在）
                if (cardPropsOnClick) {
                    console.log(`[GenericCardsStatistic] Calling cardProps.onClick for card ${item.key}`);
                    cardPropsOnClick(event);
                }
            };
            const handleCardKeyDown = (event) => {
                if (isClickable) {
                    handleKeyDown(event);
                }
                if (cardPropsOnKeyDown) {
                    cardPropsOnKeyDown(event);
                }
            };
            const handleCardMouseEnter = (event) => {
                handleMouseEnter(event);
                if (cardPropsOnMouseEnter) {
                    cardPropsOnMouseEnter(event);
                }
            };
            return (_jsx("div", { className: "statistic-card", style: mergedCardStyle, onClick: isClickable || cardPropsOnClick ? handleCardClick : undefined, onKeyDown: isClickable || cardPropsOnKeyDown ? handleCardKeyDown : undefined, onMouseEnter: handleCardMouseEnter, role: isClickable ? "button" : otherCardProps?.role, tabIndex: isClickable ? 0 : otherCardProps?.tabIndex, ...otherCardProps, children: statisticContent }));
        }
        // 否则直接返回统计内容
        return statisticContent;
    };
    // 如果没有数据，返回空
    if (!data || data.length === 0) {
        return null;
    }
    return (_jsx("div", { className: `statistic-cards-container ${className}`, style: {
            display: "flex",
            flexWrap: "wrap",
            marginLeft: -gutter[0] / 2,
            marginRight: -gutter[0] / 2,
            ...style,
        }, children: data.map((item) => (_jsx("div", { className: "statistic-card-wrapper", style: {
                width: `${100 / columns}%`,
                paddingLeft: gutter[0] / 2,
                paddingRight: gutter[0] / 2,
                marginBottom: gutter[1],
            }, children: renderStatisticCard(item) }, item.key))) }));
};
export default GenericCardsStatistic;
